import { type ReactNode, useId, useCallback, useState } from 'react';
import { css, keyframes, type Theme } from '@emotion/react';
import * as RadixAccordion from '@radix-ui/react-accordion';
import { CollapseIcon } from 'client/components/Other/CollapseIcon';
import { Switch } from 'client/components/Switch/Switch';
import { Typography } from 'client/components/Typography';

export interface AccordionProps {
  title: string | JSX.Element;
  description?: string | null;
  open?: boolean;
  hideToggle?: boolean;
  animate?: boolean;
  children?: ReactNode;
  onChange?: ((open: boolean) => void) | null;
}

export const Accordion = ({
  title,
  description = null,
  open: openProp,
  hideToggle = false,
  animate = true,
  onChange = null,
  children,
  ...other
}: AccordionProps) => {
  const id = useId();
  const [openState, setOpenState] = useState(false);

  const open = openProp === undefined ? openState : openProp;

  const handleChange = useCallback(
    (value: string) => {
      const openValue = !!value;
      setOpenState(openValue);
      if (onChange) onChange(openValue);
    },
    [onChange]
  );

  const handleSwitchChange = useCallback((checked: boolean) => handleChange(checked ? id : ''), [id, handleChange]);

  return (
    <RadixAccordion.Root
      type="single"
      collapsible
      value={open ? id : ''}
      onValueChange={handleChange}
      className="accordion"
      css={accordionStyle}
      {...other}
    >
      <RadixAccordion.Item value={id} asChild>
        <section>
          <RadixAccordion.Header asChild css={[accordionHeaderStyle, animate && accordionHeaderAnimatedStyle]}>
            <header>
              <RadixAccordion.Trigger>
                <div>
                  <Typography variant="h6" style={{ marginBottom: '0rem' }}>
                    {title}
                  </Typography>
                  {description && (
                    <Typography variant="body2" component="p">
                      {description}
                    </Typography>
                  )}
                </div>
                {hideToggle && <CollapseIcon open={open} />}
                {!hideToggle && <Switch checked={open} tabIndex={-1} onChange={handleSwitchChange} css={switchStyle} />}
              </RadixAccordion.Trigger>
            </header>
          </RadixAccordion.Header>
          <RadixAccordion.Content className="accordion-content" css={[accordionContentStyle, animate && accordionContentAnimatedStyle]}>
            {children}
          </RadixAccordion.Content>
        </section>
      </RadixAccordion.Item>
    </RadixAccordion.Root>
  );
};

const accordionStyle = (theme: Theme) => css`
  border-bottom: 2px solid ${theme.color.grey[60]};
  background-color: ${theme.color.background};
`;

const accordionHeaderStyle = css`
  > button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0;
    padding-bottom: 16px;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
  }
`;

const accordionHeaderAnimatedStyle = (theme: Theme) => css`
  > button > svg {
    transition: transform 300ms ${theme.easing.springy};
  }
`;

const slideDown = keyframes`
  from {
		height: 0;
	}
	to {
		height: var(--radix-accordion-content-height);
	}
`;

const slideUp = keyframes`
  from {
		height: var(--radix-accordion-content-height);
	}
	to {
		height: 0;
	}
`;

const accordionContentStyle = css`
  overflow: hidden;

  &[data-state='open'] {
    padding-bottom: 16px;
  }
`;

const accordionContentAnimatedStyle = (theme: Theme) => css`
  transition: padding 300ms ${theme.easing.basic}, margin 300ms ${theme.easing.basic};

  &[data-state='open'] {
    animation: ${slideDown} 400ms ${theme.easing.basic};
  }
  &[data-state='closed'] {
    animation: ${slideUp} 400ms ${theme.easing.basic};
  }
`;

const switchStyle = css`
  pointer-events: none;
`;
